import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, ClockIcon, UsersIcon, WalletIcon } from '../components/Icons';

// ─── Feature carousel content (left panel) ───────────────────────────────────
const SLIDES = [
  {
    Icon: ClockIcon,
    title: 'Time & Attendance',
    desc: 'Track attendance day by day, lock months, and let clients submit hours from their own portal',
  },
  {
    Icon: UsersIcon,
    title: 'Employee Management',
    desc: 'Onboard employees, track documents and iqama expiries, and keep every record in one place',
  },
  {
    Icon: WalletIcon,
    title: 'Payroll Management',
    desc: 'Generate payroll from attendance, finalize months, and export payslips in one click',
  },
];

// Simple decorative illustration plate — icon on a layered disc
function SlideArt({ Icon }) {
  return (
    <div className="relative w-40 h-40 mx-auto mb-8">
      <div className="absolute inset-0 rounded-full bg-white/15" />
      <div className="absolute inset-4 rounded-full bg-white/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-24 h-24 rounded-3xl bg-white flex items-center justify-center shadow-xl"
          style={{ transform: 'rotate(-6deg)' }}>
          <Icon size={44} className="text-[#e8614a]" />
        </div>
      </div>
      {/* floating accents */}
      <div className="absolute -top-1 right-3 w-5 h-5 rounded-full bg-white/40"
        style={{ animation: 'floatDot 3.5s ease-in-out infinite' }} />
      <div className="absolute bottom-2 -left-2 w-3 h-3 rounded-full bg-white/30"
        style={{ animation: 'floatDot 4.2s 0.8s ease-in-out infinite' }} />
      <div className="absolute top-8 -right-4 w-8 h-2 rounded-full bg-white/25"
        style={{ animation: 'floatDot 5s 0.4s ease-in-out infinite' }} />
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('idle');   // idle | success
  const [shakeKey, setShakeKey] = useState(0);
  const [slide, setSlide] = useState(0);

  // auto-rotate the carousel
  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const update = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading || phase === 'success') return;
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      setPhase('success');
      setTimeout(() => navigate('/'), 850);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
      setShakeKey(k => k + 1);
      setLoading(false);
    }
  }

  const S = SLIDES[slide];

  return (
    <div className="min-h-screen flex"
      style={{
        backgroundColor: '#0f1b33',
        backgroundImage: `radial-gradient(circle at 15% 10%, rgba(251,134,109,0.16), transparent 40%),
          linear-gradient(135deg, rgba(255,255,255,0.045) 25%, transparent 25%),
          linear-gradient(225deg, rgba(255,255,255,0.045) 25%, transparent 25%),
          linear-gradient(315deg, rgba(255,255,255,0.03) 25%, transparent 25%),
          linear-gradient(45deg, rgba(255,255,255,0.03) 25%, transparent 25%)`,
        backgroundPosition: '0 0, -90px 0, -90px 0, 0 0, 0 0',
        backgroundSize: '100% 100%, 180px 180px, 180px 180px, 180px 180px, 180px 180px',
      }}>

      <style>{`
        @keyframes cardShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-10px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(4px); }
          75% { transform: translateX(-2px); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wipeExpand {
          from { transform: translate(-50%, -50%) scale(0); }
          to   { transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes checkPop {
          0%   { transform: scale(0) rotate(-30deg); opacity: 0; }
          60%  { transform: scale(1.25) rotate(6deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes checkDraw { to { stroke-dashoffset: 0; } }
        @keyframes btnShimmer {
          from { transform: translateX(-100%) skewX(-15deg); }
          to   { transform: translateX(250%) skewX(-15deg); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes floatDot {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }
        @keyframes patternDrift {
          from { background-position: 0 0; }
          to   { background-position: 120px 120px; }
        }
      `}</style>

      {/* ── Success wipe ── */}
      {phase === 'success' && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
          <div className="absolute left-1/2 top-1/2 rounded-full"
            style={{
              width: '250vmax', height: '250vmax',
              background: 'radial-gradient(circle, #fb866d 0%, #e8614a 55%, #0f1b33 100%)',
              animation: 'wipeExpand 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards',
            }} />
          <div className="relative z-10" style={{ animation: 'checkPop 0.5s 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}>
            <svg width="96" height="96" viewBox="0 0 24 24" fill="none"
              stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" opacity="0.35" />
              <path d="M7 12.5 10.5 16 17 8.5"
                style={{ strokeDasharray: 24, strokeDashoffset: 24, animation: 'checkDraw 0.35s 0.5s ease-out forwards' }} />
            </svg>
          </div>
        </div>
      )}

      {/* ══ LEFT — brand panel with feature carousel (hidden on small screens) ══ */}
      <div className="hidden lg:flex w-1/2 relative flex-col items-center justify-center p-12 overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0f1b33 0%, #16233f 55%, #1d2c4e 100%)' }}>

        {/* subtle drifting geometric pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(135deg, #fb866d 25%, transparent 25%), linear-gradient(225deg, #fb866d 25%, transparent 25%)',
            backgroundSize: '120px 120px',
            animation: 'patternDrift 40s linear infinite',
          }} />
        <div className="absolute top-[8%] right-[10%] w-80 h-80 rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #fb866d, transparent 70%)' }} />
        <div className="absolute bottom-[10%] left-[5%] w-64 h-64 rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #fb866d, transparent 70%)' }} />

        {/* Feature card */}
        <div className="relative w-full max-w-md rounded-3xl px-10 py-12 text-center shadow-2xl"
          style={{ background: 'linear-gradient(150deg, #fb866d 0%, #e8614a 100%)' }}>
          <div key={slide} style={{ animation: 'fadeSlideIn 0.5s ease both' }}>
            <SlideArt Icon={S.Icon} />
            <h2 className="text-white text-2xl font-bold mb-3">{S.title}</h2>
            <p className="text-white/85 text-[14.5px] leading-relaxed">{S.desc}</p>
          </div>

          {/* dashes */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{ width: i === slide ? 28 : 14, background: i === slide ? '#fff' : 'rgba(255,255,255,0.4)' }} />
            ))}
          </div>
        </div>

        <p className="relative text-white/60 text-[14px] text-center max-w-md mt-12 leading-relaxed">
          A complete solution for managing your workforce — employees, clients,
          attendance, payroll and compliance in one place.
        </p>
      </div>

      {/* ══ RIGHT — sign-in form ══ */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-10 relative lg:bg-white">
        <div key={shakeKey}
          className="w-full max-w-[400px] rounded-3xl p-7 sm:p-8 bg-white/10 border border-white/15 shadow-2xl lg:bg-transparent lg:border-0 lg:shadow-none lg:rounded-none lg:p-0"
          style={{ backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', animation: shakeKey > 0 ? 'cardShake 0.45s ease' : 'fadeSlideIn 0.5s ease both' }}>

          {/* Logo + wordmark */}
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo.webp" alt="Delta" className="w-14 h-14 object-contain"
              style={{ filter: 'drop-shadow(0 2px 6px rgba(15,27,51,0.25))' }} />
            <div>
              <p className="text-white lg:text-[#0f1b33] text-xl font-extrabold leading-tight tracking-tight">Delta HR</p>
              <p className="text-white/60 lg:text-slate-400 text-[10.5px] font-semibold tracking-[1.5px] uppercase">Facility Services</p>
            </div>
          </div>

          <h1 className="text-white lg:text-[#0f1b33] text-[26px] lg:text-[28px] font-bold tracking-tight mb-8">Sign in to Delta HR</h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-white/70 lg:text-slate-400 text-[11px] font-bold uppercase tracking-wider mb-1.5">Email</label>
              <input name="email" type="email" placeholder="Email address" value={form.email} onChange={update} required
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all text-white placeholder-white/40 bg-white/10 border border-white/20 lg:text-slate-700 lg:placeholder-slate-400 lg:bg-white lg:border-slate-200 focus:border-[#fb866d] focus:ring-4 focus:ring-[#fb866d]/10" />
            </div>

            <div>
              <label className="block text-white/70 lg:text-slate-400 text-[11px] font-bold uppercase tracking-wider mb-1.5">Password</label>
              <div className="relative">
                <input name="password" type={showPw ? 'text' : 'password'} placeholder="Password"
                  value={form.password} onChange={update} required
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-all text-white placeholder-white/40 bg-white/10 border border-white/20 lg:text-slate-700 lg:placeholder-slate-400 lg:bg-white lg:border-slate-200 focus:border-[#fb866d] focus:ring-4 focus:ring-[#fb866d]/10" />
                <button type="button" onClick={() => setShowPw(v => !v)} tabIndex={-1}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${showPw ? 'text-[#fb866d]' : 'text-white/50 hover:text-white/80 lg:text-slate-400 lg:hover:text-slate-600'}`}>
                  <EyeIcon size={17} />
                </button>
              </div>
            </div>

            {error && (
              <div className="px-4 py-3 rounded-xl text-sm bg-red-500/20 border border-red-400/30 text-red-200 lg:bg-red-50 lg:border-red-200 lg:text-red-600">{error}</div>
            )}

            <button type="submit" disabled={loading || phase === 'success'}
              className="relative w-full py-3.5 rounded-xl text-white text-[15px] font-semibold overflow-hidden transition-all hover:-translate-y-0.5 active:scale-[0.98] disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #fb866d, #e8614a)',
                boxShadow: loading ? '0 0 24px rgba(251,134,109,0.55)' : '0 4px 16px rgba(251,134,109,0.35)',
              }}>
              {loading && (
                <span className="absolute inset-y-0 w-1/3 pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                    animation: 'btnShimmer 1.1s ease-in-out infinite',
                  }} />
              )}
              <span className="relative flex items-center justify-center gap-2.5">
                {loading && (
                  <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white inline-block"
                    style={{ animation: 'spin 0.7s linear infinite' }} />
                )}
                {phase === 'success' ? 'Welcome!' : loading ? 'Signing in...' : 'Sign In'}
              </span>
            </button>
          </form>

          {/* Client portal note */}
          <div className="mt-6 rounded-2xl px-5 py-4 flex items-center gap-3 bg-white/10 border border-white/15 lg:border-0 lg:bg-gradient-to-r lg:from-[#0f1b33] lg:to-[#1d2c4e]">
            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <ClockIcon size={16} className="text-[#fb866d]" />
            </div>
            <p className="text-white/80 text-[12.5px] leading-snug">
              <span className="text-white font-semibold">Client?</span> Sign in with your portal
              credentials to submit monthly attendance.
            </p>
          </div>
        </div>

        <p className="absolute bottom-4 text-white/40 lg:text-slate-400 text-[12px] text-center w-full lg:w-auto">
          © {new Date().getFullYear()} Delta Facility Services • All rights reserved
        </p>
      </div>
    </div>
  );
}